import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ChefHat, Volume2, VolumeX, CheckCircle2, RotateCcw, Clock, ChevronDown, ChevronUp, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const playBeep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [0, 0.18, 0.36].forEach(offset => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = 'sine'; o.frequency.value = 880; o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.0001, ctx.currentTime + offset);
      g.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + offset + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + offset + 0.15);
      o.start(ctx.currentTime + offset); o.stop(ctx.currentTime + offset + 0.16);
    });
  } catch { /* audio blocked by browser policy - non-fatal */ }
};

const minutesAgo = (iso) => Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));

const OrderCard = ({ o, onDone, onReopen }) => {
  const age = minutesAgo(o.created_at);
  const urgent = o.status === 'new' && age >= 15;
  return (
    <motion.div layout initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
      className={`rounded-2xl border-2 p-5 flex flex-col gap-4 ${o.status === 'done' ? 'border-white/10 bg-white/[0.03] opacity-70' : urgent ? 'border-red-500/70 bg-red-500/10' : 'border-amber-400/60 bg-[#1C1A15]'}`}
      data-testid={`kitchen-order-${o.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-3xl font-black text-white leading-none" style={{ fontFamily: 'Outfit' }}>Table {o.table}</p>
          <p className="text-sm text-amber-200/90 mt-1.5 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />{o.customer_name}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-white/50 font-mono">#{o.number}</p>
          <p className={`text-sm font-semibold flex items-center gap-1 justify-end mt-1 ${urgent ? 'text-red-400' : 'text-white/70'}`}><Clock className="w-3.5 h-3.5" />{age} min</p>
        </div>
      </div>
      <ul className="space-y-2">
        {o.items.map((it, i) => (
          <li key={`${it.name}-${i}`} className="flex gap-3 items-start">
            <span className="text-xl font-black text-amber-400 w-9 shrink-0" style={{ fontFamily: 'Outfit' }}>{it.qty}×</span>
            <div className="min-w-0">
              <p className="text-lg text-white font-semibold leading-snug">{it.name}</p>
              {it.comment && <p className="text-sm text-amber-300 italic mt-0.5">→ {it.comment}</p>}
            </div>
          </li>
        ))}
      </ul>
      {o.note && <p className="text-sm text-black font-semibold bg-amber-300 rounded-xl px-3 py-2" data-testid="kitchen-order-note">⚠ {o.note}</p>}
      <div className="flex items-center justify-between mt-auto pt-1">
        <span className="text-white/60 text-sm font-semibold">{o.total.toFixed(2)}€ · à la caisse</span>
        {o.status === 'new' ? (
          <button onClick={() => onDone(o.id)} className="px-5 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-base flex items-center gap-2 transition-colors active:scale-95" data-testid={`kitchen-done-${o.id}`}>
            <CheckCircle2 className="w-5 h-5" />Terminée
          </button>
        ) : (
          <button onClick={() => onReopen(o.id)} className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm flex items-center gap-2 transition-colors" data-testid={`kitchen-reopen-${o.id}`}>
            <RotateCcw className="w-4 h-4" />Rouvrir
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default function KitchenPage() {
  const { slug, token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [sound, setSound] = useState(true);
  const [showDone, setShowDone] = useState(false);
  const knownIds = useRef(null);
  const soundRef = useRef(true);
  soundRef.current = sound;

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/menu/${slug}/kitchen/${token}/orders`);
      const ids = new Set(d.pending.map(o => o.id));
      if (knownIds.current && soundRef.current && [...ids].some(id => !knownIds.current.has(id))) playBeep();
      knownIds.current = ids;
      setData(d); setError('');
    } catch (e) {
      setError(e.response?.status === 404 ? 'Écran cuisine introuvable (lien invalide)' : 'Connexion perdue, nouvelle tentative…');
    }
  }, [slug, token]);

  useEffect(() => { load(); const id = setInterval(load, 5000); return () => clearInterval(id); }, [load]);

  const setStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/menu/${slug}/kitchen/${token}/orders/${orderId}/status`, { status });
      load();
    } catch {
      setError('Action impossible, vérifiez la connexion');
    }
  };

  if (error && !data) return <div className="min-h-screen bg-[#0F0E0B] flex items-center justify-center text-white/70 text-lg" data-testid="kitchen-error">{error}</div>;
  if (!data) return <div className="min-h-screen bg-[#0F0E0B] flex items-center justify-center text-white/50">Chargement…</div>;

  return (
    <div className="min-h-screen bg-[#0F0E0B] text-white" data-testid="kitchen-page">
      <header className="sticky top-0 z-10 bg-[#0F0E0B]/95 backdrop-blur border-b border-white/10 px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {data.logo_url ? <img src={data.logo_url} alt="" className="w-11 h-11 rounded-full ring-2 ring-amber-400/60" /> : <ChefHat className="w-8 h-8 text-amber-400" />}
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ fontFamily: 'Outfit' }}>{data.business_name} · Cuisine</h1>
            <p className="text-xs text-white/50">{error || 'Mise à jour automatique toutes les 5 s'}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="px-4 py-2 rounded-full bg-amber-400 text-black font-black text-lg" data-testid="kitchen-pending-count">{data.pending.length}</span>
          <button onClick={() => setSound(s => !s)} className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" data-testid="kitchen-sound-toggle">
            {sound ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5 text-white/50" />}
          </button>
        </div>
      </header>

      <main className="p-5">
        {data.pending.length === 0 ? (
          <div className="text-center py-24 text-white/40" data-testid="kitchen-empty">
            <ChefHat className="w-16 h-16 mx-auto mb-4 opacity-40" />
            <p className="text-xl">Aucune commande en attente</p>
          </div>
        ) : (
          <motion.div layout className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            <AnimatePresence>{data.pending.map(o => <OrderCard key={o.id} o={o} onDone={id => setStatus(id, 'done')} onReopen={id => setStatus(id, 'new')} />)}</AnimatePresence>
          </motion.div>
        )}

        {data.done.length > 0 && (
          <section className="mt-10">
            <button onClick={() => setShowDone(s => !s)} className="flex items-center gap-2 text-white/60 hover:text-white text-sm font-semibold mb-4" data-testid="kitchen-toggle-done">
              {showDone ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}Terminées aujourd'hui ({data.done.length})
            </button>
            {showDone && (
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {data.done.map(o => <OrderCard key={o.id} o={o} onDone={id => setStatus(id, 'done')} onReopen={id => setStatus(id, 'new')} />)}
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
